import React, { useEffect, useState } from 'react';
import ActionBar from '../layout/ActionBar';
import GameList from './components/GameList';
import EditGame from './slideout/EditGame';

import { hasPermission, userIs } from '../auth/authUtils';
import permissionTypes from '../../constants/permissionTypes';
import SlidingPane from 'react-sliding-pane';
import userTypes from '../../constants/userTypes';
import SteamBulkRequest from '../requests/slideout/SteamBulkRequest';
import { toast } from 'react-toastify';
import GameBulkActions from './slideout/GameBulkActions';
import { populateSkuKeyPools } from '../../util/steamUtils';
import RequestKeys from '../requests/slideout/RequestKeys';
import Button from '../common/Button';
import { useTranslation } from 'react-i18next';
import { usePageTitle } from '../../util/pageTitle';
import { useLocalStorage } from '../../hooks/useLocalStorage';
import GameListWithSku from './components/GameListWithSku';
import GameListTableViewSwitcher from './components/GameListTableViewSwitcher';
import { useAppSelector } from '../../hooks';
import { useSearchParams, useNavigate } from 'react-router-dom';
import GameFranchise from './slideout/GameFranchise';
import SteamGameImport from './slideout/SteamGameImport';

export interface GamesProps {
  setCompleteRequestSlideoutOpen: (open: boolean) => void;
}

function Games({ setCompleteRequestSlideoutOpen }: GamesProps) {
  const { t } = useTranslation();
  usePageTitle(['Games']);
  const navigate = useNavigate();
  const currentUser = useAppSelector((state) => state.auth);
  const [searchParams] = useSearchParams();

  const [groupBy, setGroupBy] = useLocalStorage<'games' | 'sku'>('games-groupBy', 'games');

  const [reload, setReload] = useState<any>();
  const [addGameSlideoutOpen, setAddGameSlideoutOpen] = useState(false);
  const [steamGameImportSlideoutOpen, setSteamGameImportSlideoutOpen] = useState(false);
  const [itemsChecked, setItemsChecked] = useState<any[]>([]);
  const [bulkSteamRequestSlideoutOpen, setBulkSteamRequestSlideoutOpen] =
    useState(false);
  const [bulkSteamRequestSlideoutSKUs, setBulkSteamRequestSlideoutSKUs] =
    useState<any>();
  const [bulkActionsSlideoutOpen, setBulkActionsSlideoutOpen] = useState(false);
  const [requestKeysSlideoutOpen, setRequestKeysSlideoutOpen] = useState(false);
  const [requestKeysGameId, setRequestKeysGameId] = useState<any>(false);
  const [requestKeysSkuId, setRequestKeysSkuId] = useState<any>(false);
  const [addGameFranchiseSlideoutOpen, setAddGameFranchiseSlideoutOpen] = useState(false);
  const [onFranchiseCreatedCallback, setOnFranchiseCreatedCallback] = useState<{ fn: (franchise: any) => void } | null>(null);

  useEffect(() => {
    const groupByParam = searchParams.get('groupBy');
    if (groupByParam && (groupByParam === 'games' || groupByParam === 'sku')) {
      setGroupBy(groupByParam);
    }
  }, [searchParams, setGroupBy]);

  const handleSteamBulkRequest = async (e: any, games: any[]) => {
    e.cancelBubble = true;
    const skus = await populateSkuKeyPools(
      games.map((x: any) => {
        return {
          gameId: x.gameId,
          gameName: x.gameName,
          skus: x.skuIds ?? [x.skuId],
        };
      })
    );
    if (skus.length > 0) {
      setBulkSteamRequestSlideoutSKUs(skus);
      setBulkSteamRequestSlideoutOpen(true);
    } else {
      toast.error(
        t(
          'None of the selected SKUs support key pools or the workflow is missing key provider reference'
        )
      );
    }
    return false;
  };

  useEffect(() => {
    setItemsChecked([]);
  }, [groupBy]);

  return (
    <>
      <ActionBar breadcrumb={[{ link: '/games', label: t('Games') }]}>
        {hasPermission(currentUser, permissionTypes.MANAGE_GAMES) && (
          <>
            <Button
              className="btn btn-default me-2"
              onClick={() => setSteamGameImportSlideoutOpen(true)}
              text={t('Import from Steam')}
              testId="steam-game-import-button"
            /><Button
              className="btn btn-primary me-2"
              onClick={() => setAddGameSlideoutOpen(true)}
              text={t('Add game')}
              testId="add-game-button"
            />
          </>
        )}
      </ActionBar>
      {groupBy === 'games' ? (
        <GameList
          reload={reload}
          itemsChecked={itemsChecked}
          setItemsChecked={setItemsChecked}
          onRequestKeys={(e: any, item: any) => {
            setRequestKeysGameId(item.gameId);
            setRequestKeysSkuId(undefined);
            setRequestKeysSlideoutOpen(true);
          }}
          onSteamBulkRequest={handleSteamBulkRequest}
          setBulkActionsSlideoutOpen={
            userIs(currentUser, userTypes.UPLOADER)
              ? setBulkActionsSlideoutOpen
              : () => { }
          }
          tableViewSwitcher={
            <GameListTableViewSwitcher
              groupBy={groupBy}
              setGroupBy={setGroupBy}
            />
          }
        />
      ) : (
        <GameListWithSku
          reload={reload}
          itemsChecked={itemsChecked}
          setItemsChecked={setItemsChecked}
          onRequestKeys={(e: any, item: any) => {
            setRequestKeysGameId(item.gameId);
            setRequestKeysSkuId(item.skuId);
            setRequestKeysSlideoutOpen(true);
          }}
          onSteamBulkRequest={handleSteamBulkRequest}
          setBulkActionsSlideoutOpen={
            userIs(currentUser, userTypes.UPLOADER)
              ? setBulkActionsSlideoutOpen
              : () => { }
          }
          tableViewSwitcher={
            <GameListTableViewSwitcher
              groupBy={groupBy}
              setGroupBy={setGroupBy}
            />
          }
        />
      )}

      <SlidingPane
        isOpen={addGameSlideoutOpen}
        hideHeader={true}
        from="right"
        className="side-panel"
        onRequestClose={() => setAddGameSlideoutOpen(false)}
      >
        <EditGame
          id={undefined}
          done={(savedGame: any) => {
            if (savedGame) {
              setAddGameSlideoutOpen(false);
              navigate(`/game/${savedGame.id}`);
            }
          }}
          cancel={() => setAddGameSlideoutOpen(false)}
          addGameFranchise={() => {
            setAddGameFranchiseSlideoutOpen(true);
          }}
          registerOnFranchiseCreated={(cb) => setOnFranchiseCreatedCallback(cb ? { fn: cb } : null)}
        />
      </SlidingPane>

      <SlidingPane
        isOpen={steamGameImportSlideoutOpen}
        hideHeader={true}
        from="right"
        className="large-side-panel"
        onRequestClose={() => setSteamGameImportSlideoutOpen(false)}
      >
        <SteamGameImport cancel={() => setSteamGameImportSlideoutOpen(false)} done={() => { }} />
      </SlidingPane>


      <SlidingPane
        isOpen={bulkActionsSlideoutOpen}
        hideHeader={true}
        from="right"
        className="small-side-panel"
        onRequestClose={() => setBulkActionsSlideoutOpen(false)}
      >
        <GameBulkActions
          done={() => {
            setReload(new Date());
            setBulkActionsSlideoutOpen(false);
          }}
          cancel={() => setBulkActionsSlideoutOpen(false)}
          items={itemsChecked}
        ></GameBulkActions>
      </SlidingPane>
      <SlidingPane
        isOpen={bulkSteamRequestSlideoutOpen}
        hideHeader={true}
        from="right"
        className="xlarge-side-panel"
        onRequestClose={() => setBulkSteamRequestSlideoutOpen(false)}
      >
        {bulkSteamRequestSlideoutSKUs ? (
          <SteamBulkRequest
            gameName={bulkSteamRequestSlideoutSKUs?.[0]?.gameName ?? ''}
            done={() => {
              setReload(new Date());
              setBulkSteamRequestSlideoutSKUs(null);
            }}
            cancel={() => setBulkSteamRequestSlideoutOpen(false)}
            skus={bulkSteamRequestSlideoutSKUs}
          ></SteamBulkRequest>
        ) : (
          <></>
        )}
      </SlidingPane>
      <SlidingPane
        isOpen={requestKeysSlideoutOpen}
        hideHeader={true}
        from="right"
        className="small-side-panel"
        onRequestClose={() => setRequestKeysSlideoutOpen(false)}
      >
        <RequestKeys
          gameId={requestKeysGameId}
          skuId={requestKeysSkuId}
          done={() => {
            setRequestKeysSlideoutOpen(false);
            setCompleteRequestSlideoutOpen(true);
          }}
          cancel={() => setRequestKeysSlideoutOpen(false)}
        />
      </SlidingPane>

      <SlidingPane
        isOpen={addGameFranchiseSlideoutOpen}
        hideHeader={true}
        from="right"
        className="side-panel"
        onRequestClose={() => setAddGameFranchiseSlideoutOpen(false)}
      >
        <GameFranchise
          cancel={() => setAddGameFranchiseSlideoutOpen(false)}
          done={(savedFranchise) => {
            setAddGameFranchiseSlideoutOpen(false);
            onFranchiseCreatedCallback?.fn?.(savedFranchise);
          }}
        ></GameFranchise>
      </SlidingPane>
    </>
  );
}

export default Games;
